import { useRef, useState } from "react";
import { Link } from "@/i18n/LocaleLink";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BookingSection, PlacesLeft } from "@/components/BookingForm";
import { BIRTH_ART } from "@/data/birthArt";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/hooks/useSeo";

const BookButton = ({ label, testid }) => (
  <a
    href="#book"
    data-testid={testid}
    className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust"
  >
    {label}
    <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

const Hero = ({ ba }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden grain" data-testid="ba-hero">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-32">
        <img src="/images/birth-art.jpg" alt="Hands working with soft pastels, tea and candlelight at a Birth Art gathering" className="h-full w-full object-cover scale-105" />
        <div className="absolute inset-0 bg-ink/40" />
        <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-ink/70 to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-6 pb-20 pt-40 lg:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mb-6 max-w-xl text-xs tracking-[0.3em] uppercase text-cream/85"
          data-testid="ba-hero-eyebrow"
        >
          {ba.hero.eyebrow}
        </motion.p>
        <div className="overflow-hidden">
          <motion.h1
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-cream text-5xl sm:text-6xl lg:text-7xl leading-[1.0] tracking-tight"
            data-testid="ba-hero-title"
          >
            {ba.hero.title}
          </motion.h1>
        </div>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8 }}
          className="mt-7 max-w-2xl text-lg leading-relaxed text-cream"
          data-testid="ba-hero-intro"
        >
          {ba.hero.intro}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm tracking-wide text-sand"
          data-testid="ba-hero-facts"
        >
          {ba.hero.facts.map((f, i) => (
            <span key={f} className="flex items-center gap-5">
              {i > 0 && <span className="text-cream/40">·</span>}
              <span className={f.includes("€") ? "font-medium text-cream" : ""}>{f}</span>
            </span>
          ))}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-9 flex flex-wrap items-center gap-6"
        >
          <BookButton label={ba.hero.primaryCta} testid="ba-hero-book" />
          <a href="#next-gathering" data-testid="ba-hero-next"
            className="group inline-flex items-center gap-2 text-sm tracking-wide text-cream/85 hover:text-cream transition-colors duration-300 border-b border-cream/40 pb-1">
            {ba.hero.secondaryCta}
            <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-ink/10" data-testid={`ba-faq-${index}`}>
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center justify-between gap-6 py-6 text-left" data-testid={`ba-faq-toggle-${index}`}>
        <span className="font-serif text-xl sm:text-2xl text-ink">{item.q}</span>
        <ChevronDown size={18} strokeWidth={1.5} className={`shrink-0 text-clay transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
            <p className="pb-7 pr-10 text-base leading-relaxed text-soft">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function BirthArtPage() {
  const { lang } = useLang();
  const ba = BIRTH_ART[lang] || BIRTH_ART.en;
  useSeo({ ...ba.seo, image: `/images/birth-art.jpg` });

  return (
    <main data-testid="birth-art-page">
      <Hero ba={ba} />

      <BookingSection
        offer="birth-art"
        banner={
          <div id="next-gathering" className="mb-10 border-b border-ink/10 pb-8" data-testid="ba-booking-band">
            <p className="text-xs tracking-[0.3em] uppercase text-clay mb-5">{ba.next.heading}</p>
            <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
              <p className="font-serif text-2xl sm:text-3xl text-ink" data-testid="ba-next-date">{ba.next.date}</p>
              <div className="text-sm leading-relaxed text-soft">
                <p>{ba.next.time} · {ba.next.venue}</p>
                <p>{ba.next.line}</p>
                <p>{ba.next.note}</p>
              </div>
              <span className="rounded-full border border-sage/40 bg-sage/10 px-4 py-1.5 text-xs tracking-wide text-sage" data-testid="ba-next-spots"><PlacesLeft offer="birth-art" dates={[
                { value: "2026-11-07", short: lang === "nl" ? "7 nov" : "7 Nov" },
                { value: "2026-12-12", short: lang === "nl" ? "12 dec" : "12 Dec" },
              ]} /></span>
            </div>
          </div>
        }
        dates={[
          { value: "2026-11-07", label: lang === "nl" ? "zaterdag 7 november 2026" : "Saturday 7 November 2026" },
          { value: "2026-12-12", label: lang === "nl" ? "zaterdag 12 december 2026" : "Saturday 12 December 2026" },
        ]}
        tickets={null}
        priceLabel="€45"
      />

      <section className="bg-cream py-28 lg:py-40" data-testid="ba-heart">        <div className="mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.2] tracking-tight text-ink">{ba.heart.title}</h2>
            <p className="mt-6 font-serif italic text-2xl sm:text-3xl leading-snug text-clay">{ba.heart.sub}</p>
            <p className="mt-10 text-base leading-relaxed text-soft">{ba.heart.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ba.heart.p2}</p>
            <p className="mt-12 font-serif italic text-xl sm:text-2xl leading-snug text-ink/80">{ba.heart.closing}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-20 lg:py-24" data-testid="ba-testimonials">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ba.testimonials.heading}</h2>
            <div className="mt-12 text-left max-w-2xl mx-auto">
              {ba.testimonials.items.map((t, i) => (
                <blockquote key={i} data-testid={`ba-testimonial-${i}`}>
                  <p className="font-serif italic text-5xl leading-none text-clay/40" aria-hidden>"</p>
                  <p className="mt-2 font-serif italic text-lg leading-relaxed text-ink/85 whitespace-pre-line">{t.text}</p>
                  <footer className="mt-5 text-xs tracking-[0.25em] uppercase text-clay">— {t.name}</footer>
                </blockquote>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="ba-why">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl leading-snug tracking-tight text-ink">{ba.why.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft drop-cap">{ba.why.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ba.why.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ba.why.p3}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-32" data-testid="ba-happens">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-clay mb-4">{ba.happens.heading}</p>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ba.happens.title}</h2>
          </Reveal>
          <div className="mt-10 space-y-8">
            {ba.happens.steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <p className="text-base leading-relaxed text-soft border-l-2 border-clay/30 pl-6" data-testid={`ba-step-${i}`}>{s}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="ba-cant-draw">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <div className="overflow-hidden rounded-t-[10rem]">
              <img loading="lazy" src="/images/birth-art.jpg" alt="Pastels, paper and tea on the Birth Art table" className="aspect-[4/5] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 lg:col-start-8">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ba.cantDraw.title}</h2>
            <p className="mt-7 font-serif text-2xl leading-snug text-clay italic">{ba.cantDraw.p1}</p>
            <p className="mt-6 text-base leading-relaxed text-soft">{ba.cantDraw.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ba.cantDraw.p3}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ba.cantDraw.p4}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-32" data-testid="ba-different">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ba.different.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{ba.different.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ba.different.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ba.different.p3}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="ba-emerge">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ba.emerge.title}</h2>
            <p className="mt-7 font-serif italic text-2xl text-clay">{ba.emerge.p1}</p>
            <p className="mt-7 text-base leading-relaxed text-soft">{ba.emerge.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ba.emerge.p3}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream pb-24 lg:pb-32" data-testid="ba-crosslink">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <div className="border border-ink/10 bg-parchment/60 p-10 lg:p-14 text-center">
              <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">{ba.crossLink.title}</h2>
              <p className="mt-5 text-sm leading-relaxed text-soft max-w-xl mx-auto">{ba.crossLink.body}</p>
              <Link to="/offerings/active-birth" data-testid="ba-crosslink-cta"
                className="group mt-7 inline-flex items-center gap-3 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust">
                {ba.crossLink.cta}
                <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-32" data-testid="ba-irida">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="relative">
              <div className="absolute -inset-4 rounded-t-[10rem] border border-clay/30" aria-hidden />
              <div className="overflow-hidden rounded-t-[10rem]">
                <img loading="lazy" src="/irida.jpg" alt="Irida Hysenbelli, founder of Women's Den" className="aspect-[3/4] w-full object-cover" data-testid="ba-irida-photo" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 lg:col-start-6">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ba.irida.heading}</h2>
            <p className="mt-6 text-base leading-relaxed text-soft drop-cap">{ba.irida.body}</p>
            <Link to="/about" data-testid="ba-irida-link"
              className="group mt-8 inline-flex items-center gap-3 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust">
              {ba.irida.link}
              <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="ba-faq">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink mb-8">{ba.faq.heading}</h2>
          </Reveal>
          <Reveal>
            <div>
              {ba.faq.items.map((item, i) => <FaqItem key={i} item={item} index={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-24 lg:py-36 grain relative" data-testid="ba-final">
        <div className="mx-auto max-w-3xl px-6 text-center relative z-10">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-cream">{ba.final.title}</h2>
            <p className="mt-10 text-xs tracking-[0.3em] uppercase text-sand/80">{ba.final.heading}</p>
            <p className="mt-4 font-serif text-2xl text-cream">{ba.final.line1}</p>
            <p className="mt-2 text-sm text-cream/70">{ba.final.line2}</p>
            <p className="mt-1 text-sm text-sand/70 italic font-serif">{ba.final.note}</p>
            <div className="mt-10">
              <BookButton label={ba.final.cta} testid="ba-final-book" />
            </div>
            <Link to="/contact?topic=Birth%20Art" data-testid="ba-final-contact"
              className="group mt-8 inline-flex items-center gap-2 text-sm text-sand hover:text-cream transition-colors duration-300">
              {ba.final.secondary}
              <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
